import React, { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import StaticPageComponent from '@fuww/library/src/StaticPage';
import Headline3 from '@fuww/library/src/Typography/Heading/Headline3';
import SpacingContainer from '@fuww/library/src/SpacingContainer';
import HomeLink from '../components/HomeLink';
import Markdown from '../components/Markdown';
import markdown
  from '../data/staticPages/landing/fashionweeks-around-the-world-list.md';
import Head from '../components/Head';
import messages from '../lib/messages.mjs';
import Layout from '../components/Layout';
import Hero from '../components/Hero';
import withQuery from '../lib/withQuery';
import ArticleList from '../components/News/Article/List';
import { useSiteContext } from '../components/SiteContext';
import {
  newsFeedImageConfigurations,
} from '../lib/imageConfigurations';
import articlesConnectionQuery
  from '../lib/queries/news/articlesConnectionQuery.graphql';
import throw404 from '../lib/throw404';
import {
  NewsArticleSorting,
  NewsArticleStatus,
} from '../lib/graphql/api/graphql';

const headerImageUrls = {
  mobile: [
    'https://r.fashionunited.com/e3foA-Y29KDVByIlBvqkryu0C886jZloiTqOeUuTSrA/resize:fill:42:34:0/gravity:ce/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbndlZWtzX21vYmlsZS5qcGc',
    'https://r.fashionunited.com/U4xWL60v7q4eKKxU30AAtD2w0AxM9QT5hLD6T6uTbxg/resize:fill:360:288:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbndlZWtzX2Jhbm5lcl9tb2JpbGUuanBn',
    'https://r.fashionunited.com/euHcBQ9w3fifzeWlx26NFB1vj8U9SN8DBw8HxGBWkTc/resize:fill:720:576:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbndlZWtzX2Jhbm5lcl9tb2JpbGUuanBn',
  ],
  desktop: [
    'https://r.fashionunited.com/_cSH-KQMmPTY_2LWPtFFlqNwxDull_Ulu2QVi1ih5U8/resize:fill:42:8:0/gravity:ce/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbndlZWtzX2Jhbm5lcl9kZXNrdG9wLmpwZw',
    'https://r.fashionunited.com/QGnUW23NY1uXDxZP_OSdIBHrL2dca5tKeJI26rXJ8X4/resize:fill:1350:277:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbndlZWtzX2Jhbm5lcl9kZXNrdG9wLmpwZw',
    'https://r.fashionunited.com/7JunCSIeKXLl9tgMF87th1Up9_LSAGvmgULP5-ll6os/resize:fill:2560:526:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbndlZWtzX2Jhbm5lcl9kZXNrdG9wLmpwZw',
    'https://r.fashionunited.com/PtjtgORoj3npIo_ys8jvEoLBuPVVuxt-ltBXnhDulK4/resize:fill:5120:1052:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbndlZWtzX2Jhbm5lcl9kZXNrdG9wLmpwZw',
  ],
};

const ArticleListWithQuery = withQuery(ArticleList);

const FashionWeeksAroundTheWorldListPage = () => {
  const intl = useIntl();
  const {
    newsboard, locale,
  } = useSiteContext();

  const breadcrumbs = [
    <HomeLink />,
    intl.formatMessage(messages['fashionweeks-around-the-world-list.title']),
  ];

  if (locale !== 'en-US') return throw404();

  return (
    <>
      <Head
        title={intl.formatMessage(
          messages['fashionweeks-around-the-world-list.title'],
        )}
        description={intl.formatMessage(
          messages['fashionweeks-around-the-world-list.description'],
        )}
      />
      <StaticPageComponent
        breadcrumbs={breadcrumbs}
        title={intl.formatMessage(
          messages['fashionweeks-around-the-world-list.title'],
        )}
        content={(
          <Markdown markdown={markdown} allowGfm allowHtml />
        )}
      >
        <SpacingContainer marginBottom="5rem" />
        <Headline3 align="center">
          {intl.formatMessage(
            messages['fashionweeks-around-the-world-list.latestNews'],
          )}
        </Headline3>
        <SpacingContainer />
        <ArticleListWithQuery
          query={articlesConnectionQuery}
          variables={{
            newsboard,
            notInIds: [],
            statuses: [
              NewsArticleStatus.PUBLISHED,
              NewsArticleStatus.ARCHIVED,
            ],
            first: 20,
            sort: NewsArticleSorting.NORMAL,
            imageConfigurations: newsFeedImageConfigurations,
            pageviews: false,
            keywords: [
              'lfw',
              'londonashionweek',
              'London fashion week',
              'milan fashion week',
              'fashion week',
              'paris fashion week',
            ],
          }}
          errorMessage="Error loading news articles..."
          shouldLoadMore={false}
        />
      </StaticPageComponent>
    </>
  );
};

FashionWeeksAroundTheWorldListPage.getLayout = (page: ReactElement) => (
  <Layout
    header={(
      <Hero
        imageUrls={headerImageUrls}
        alt="Fashion Weeks around the world"
      />
    )}
  >
    {page}
  </Layout>
);

export default FashionWeeksAroundTheWorldListPage;
